import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { API } from '@/lib/api';
import {
  storeGeneratedLocalPassword,
  storePasswordVerification,
} from '@/lib/localPasswordSession';
import { toast } from 'sonner';

export default function AuthCallback() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const hash = window.location.hash;
        const sessionId = hash.split('session_id=')[1]?.split('&')[0];
        
        if (!sessionId) {
          navigate('/auth?auth_error=google_missing_session', { replace: true });
          return;
        }

        const resp = await fetch(`${API}/auth/session`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ session_id: sessionId }),
        });

        if (!resp.ok) throw new Error('Auth failed');

        const data = await resp.json();
        login(data.user, data.session_token);
        if (data.generated_local_password) {
          storeGeneratedLocalPassword(data.user.user_id, data.generated_local_password);
          toast.success('Google sign-in worked. Your local backup password is now available in Settings.');
        }
        if (data.password_verification_id && data.password_verification_purpose) {
          storePasswordVerification(
            data.user.user_id,
            data.password_verification_id,
            data.password_verification_purpose,
          );
          toast.success('Google verification complete. You can finish managing your local password now.');
        }

        // Clear hash and redirect
        window.history.replaceState(null, '', window.location.pathname);
        let destination = data.post_auth_path || (data.user.onboarding_done ? '/today' : '/onboarding');
        // If phone is missing, append a prompt flag so the receiving page can invite the user to add it.
        if (!data.user.phone) {
          const sep = destination.includes('?') ? '&' : '?';
          destination = `${destination}${sep}prompt_phone=1`;
        }
        navigate(destination, { replace: true });
      } catch (err) {
        console.error('Auth callback error:', err);
        navigate('/auth?auth_error=google_session_failed', { replace: true });
      }
    };

    processAuth();
  }, [navigate, login]);

  return (
    <div className="h-screen w-screen flex items-center justify-center" style={{ background: 'var(--bg-base)' }}>
      <div className="flex flex-col items-center gap-4">
        <div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--accent-primary)', borderTopColor: 'transparent' }} />
        <p style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>Authenticating...</p>
      </div>
    </div>
  );
}
